import { createFileRoute, Link } from "@tanstack/react-router";
import { useContent } from "@/lib/use-content";
import { ArrowLeft, BookOpen, Heart, Users } from "lucide-react";
import vintagePhoto from "@/assets/vintage-photo.png";

export const Route = createFileRoute("/")({ component: Home });

function Home() {
  const c = useContent("home");
  return (
    <>
      {/* Hero with fixed background image — page scrolls over it (parallax) */}
      <section
        className="relative isolate overflow-hidden bg-cover bg-center bg-no-repeat md:bg-fixed"
        style={{ backgroundImage: `url(${c.heroImage})` }}
      >
        <div className="absolute inset-0 gradient-hero" />
        <div className="relative container mx-auto px-4 py-32 md:py-48 text-center text-primary-foreground">
          <div className="mx-auto mb-6 h-px w-24 bg-gold" />
          <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tight drop-shadow-lg">
            {c.heroTitle}
          </h1>
          <p className="mt-6 text-xl md:text-2xl text-gold font-display italic drop-shadow">
            {c.heroSubtitle}
          </p>
          <Link
            to="/register"
            className="mt-10 inline-flex items-center gap-2 rounded-md gradient-gold px-8 py-3 text-base font-bold text-gold-foreground shadow-elegant transition-transform hover:scale-105"
          >
            {c.heroCta}
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </div>
      </section>


      {/* About */}
      <section className="container mx-auto px-4 py-20">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          <div className="order-2 md:order-1">
            <img src={c.aboutImage} alt="היכל הישיבה" className="rounded-lg shadow-elegant w-full h-auto" loading="lazy" width={1280} height={896} />
          </div>
          <div className="order-1 md:order-2">
            <div className="mb-3 h-px w-12 bg-gold" />
            <h2 className="font-display text-4xl font-bold text-primary">{c.aboutTitle}</h2>
            <p className="mt-3 font-display text-lg text-gold font-semibold">{c.aboutSubtitle}</p>
            <div className="mt-6 space-y-4 text-foreground/80 leading-relaxed whitespace-pre-line">
              {c.aboutText}
            </div>
            <Link to="/about" className="mt-6 inline-flex items-center gap-1 text-primary font-semibold hover:text-gold transition-colors">
              קראו עוד <ArrowLeft className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Values strip — vintage photo parallax background with cards */}
      <section className="relative py-28 overflow-hidden">
        {/* Vintage grayscale+sepia background */}
        <div
          className="absolute inset-0 bg-cover bg-center md:bg-fixed"
          style={{
            backgroundImage: `url(${vintagePhoto})`,
            backgroundPosition: "center 30%",
            filter: "grayscale(100%) sepia(40%) contrast(85%) brightness(105%)",
          }}
        />
        {/* Dark vignette overlay */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.35) 100%)" }} />
        <div className="relative container mx-auto px-4 flex justify-center">
          <div className="grid gap-y-8 md:grid-cols-3 w-full max-w-6xl" style={{ columnGap: "12rem" }}>
            {[
              { icon: BookOpen, t: "לימוד תורה", d: "תוכנית לימודים אישית בקצב ובהיקף המתאימים" },
              { icon: Heart, t: "יחס אישי", d: "ליווי חם וקרוב לכל תלמיד לאורך כל הדרך" },
              { icon: Users, t: "סביבה תומכת", d: "צוות מקצועי ומסור שעובד כמשפחה אחת" },
            ].map((v) => (
              <div key={v.t} className="text-center bg-card/92 backdrop-blur-sm rounded-xl p-7 shadow-elegant border border-border">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full gradient-gold text-gold-foreground shadow-card">
                  <v.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold text-primary">{v.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rabbi message */}
      <section className="container mx-auto px-4 py-20">
        <div className="mx-auto max-w-5xl grid gap-10 md:grid-cols-[300px_1fr] md:items-start">
          {c.rabbiImage && (
            <img src={c.rabbiImage} alt={c.rabbiTitle} className="rounded-lg shadow-elegant w-full h-auto object-cover" loading="lazy" />
          )}
          <div>
            <div className="mb-3 h-px w-12 bg-gold" />
            <h2 className="font-display text-4xl font-bold text-primary">{c.rabbiTitle}</h2>
            <div className="mt-6 space-y-4 text-lg leading-relaxed text-foreground/85 whitespace-pre-line font-display">
              {c.rabbiText}
            </div>
          </div>
        </div>
      </section>


      {/* CTA */}
      <section className="bg-primary text-primary-foreground py-20 text-center">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-4xl font-bold">מעוניינים להצטרף?</h2>
          <p className="mt-3 text-primary-foreground/80">נשמח להכיר את בנכם ולשלב אותו במסגרת</p>
          <Link to="/register" className="mt-8 inline-flex items-center gap-2 rounded-md gradient-gold px-8 py-3 font-bold text-gold-foreground shadow-elegant hover:scale-105 transition-transform">
            לטופס הרשמה <ArrowLeft className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
